/**
 * Barrel export for the UBT analytics service.
 *
 * Also declares global Window augmentations for window.digitalData and
 * window._satellite so TypeScript is satisfied project-wide without needing
 * extra reference directives.
 */

// ── Re-exports ────────────────────────────────────────────────────────────────
export * from './config';
export * from './dataLayer';
export * from './pagetracking';

// ── Imports for initializeAnalytics ──────────────────────────────────────────
import { initializeDigitalData, setUserInfo, setSiteInfo } from './dataLayer';
import { ANALYTICS_CONFIG } from './config';

// ── TypeScript Interfaces ─────────────────────────────────────────────────────

/** Shape of window.digitalData.page.pageInfo */
export interface PageInfo {
  page: string;
  pageNameGranular: string;
  pageName: string;
  pageTemplate?: string;
  pageType?: string;
  previousPage?: string;
  previousLink?: string;
  [key: string]: unknown;
}

/** Shape of window.digitalData.page.content */
export interface PageContent {
  title?: string;
  type?: string;
  [key: string]: unknown;
}

/** Shape of window.digitalData.page */
export interface DigitalDataPage {
  pageInfo?: PageInfo;
  content?: PageContent;
  [key: string]: unknown;
}

/** Shape of window.digitalData.user */
export interface DigitalDataUser {
  userID?: string;
  loginState?: 'Authenticated' | 'Unauthenticated';
  profile?: { region?: string; [key: string]: unknown };
  job?: { jobRole?: string; [key: string]: unknown };
  employee?: {
    union?: string;
    workforceClass?: string;
    employeeTenure?: string;
    [key: string]: unknown;
  };
  userKnown?: boolean;
  timestamp?: string;
  [key: string]: unknown;
}

/** Shape of window.digitalData.site */
export interface DigitalDataSite {
  siteName?: string;
  domain?: string;
  subdomain?: string;
  language?: string;
  charSet?: string;
  applicationName?: string;
  [key: string]: unknown;
}

/** Top-level window.digitalData shape */
export interface DigitalData {
  page?: DigitalDataPage;
  user?: DigitalDataUser;
  site?: DigitalDataSite;
  event?: Record<string, unknown>;
  [key: string]: unknown;
}

// ── Global window augmentation ────────────────────────────────────────────────
declare global {
  interface Window {
    digitalData?: DigitalData;
    _satellite?: {
      track: (eventName: string) => void;
      kpCustomEvent?: (eventType: string, data: Record<string, unknown>) => void;
      [key: string]: unknown;
    };
  }
}

// ── One-time initializer ──────────────────────────────────────────────────────

/** Flag that prevents initializeAnalytics from running more than once per session. */
let analyticsInitialized = false;

/**
 * Initializes the CJA analytics layer for the UBT application.
 *
 * Call this once after the user has authenticated and their identity is known.
 * Sets up window.digitalData with user and site metadata.
 *
 * @param userData - Authenticated user attributes (nuid required for Authenticated state)
 */
export const initializeAnalytics = (userData: {
  nuid?: string;
  email?: string;
  region?: string;
}): void => {
  if (analyticsInitialized) return;

  initializeDigitalData();

  setUserInfo({
    ...userData,
    loginState: userData.nuid ? 'Authenticated' : 'Unauthenticated',
  });

  setSiteInfo(ANALYTICS_CONFIG.APPS.UBT);

  analyticsInitialized = true;
  console.log('CJA Analytics initialized for UBT');
};

/**
 * Resets the initialization flag.
 * Useful in tests so each test starts with a clean analytics state.
 * @internal
 */
export const _resetAnalyticsInitialized = (): void => {
  analyticsInitialized = false;
};

